import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, AppBar, Toolbar, Typography, Container, Box, List, ListItem, ListItemIcon, ListItemText, IconButton, SwipeableDrawer, Tab } from '@mui/material';
import { Home, Portrait, Landscape, Architecture, ContactMail, ShoppingBag, Person, Menu, Instagram } from '@mui/icons-material';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import UnifiedGallery from './components/UnifiedGallery';
import Contact from './components/Contact';
import About from './components/About';

// Initial categories structure
const initialCategories = {
  portrait: {
    title: 'Portrait Photography',
    path: 'portrait'
  },
  product: {
    title: 'Product Photography',
    path: 'product'
  },
  landscape: {
    title: 'Landscape Photography',
    path: 'landscape'
  },
  architecture: {
    title: 'Architecture Photography',
    path: 'architecture'
  }
};

const menuItems = [
  { text: 'Home', icon: <Home />, path: '/' },
  { text: 'Portrait', icon: <Portrait />, path: '/portrait' },
  { text: 'Product', icon: <ShoppingBag />, path: '/product' },
  { text: 'Landscape', icon: <Landscape />, path: '/landscape' },
  { text: 'Architecture', icon: <Architecture />, path: '/architecture' },
  { text: 'About', icon: <Person />, path: '/about' },
  { text: 'Contact', icon: <ContactMail />, path: '/contact' },
];

// Theme configuration
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#E0C3FC',
      light: '#F1DEFF',
      dark: '#B388FF',
    },
    secondary: {
      main: '#8EC5FC',
      light: '#B8DCFD',
      dark: '#5A9DF9',
    },
    background: {
      default: '#1A1A1A',
      paper: '#262626',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0',
    }
  },
  typography: {
    fontFamily: '"Montserrat", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: 2,
    },
    subtitle1: {
      letterSpacing: 4,
      fontWeight: 500,
    },
    button: {
      letterSpacing: 1,
    }
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(38,38,38,0.95) 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          padding: '12px 24px',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#E0C3FC',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
  },
});

function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <List>
      {menuItems.map((item) => (
        <ListItem
          button
          key={item.text}
          component={Link}
          to={item.path}
          onClick={handleDrawerToggle}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          right: { xs: '20px', sm: '40px' },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1200,
          backgroundColor: 'rgba(38,38,38,0.8)',
          borderRadius: '50%',
          padding: '8px',
          backdropFilter: 'blur(5px)',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(38,38,38,0.95)',
            transform: 'translateY(-50%) scale(1.1)'
          }
        }}
      >
        <IconButton
          color="inherit"
          aria-label="Instagram"
          component="a"
          href="https://www.instagram.com/kva_clixz?igsh=anQ3bmtwNG1qejNu"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
            padding: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            '&:hover': {
              transform: 'scale(1.1)',
              background: 'linear-gradient(45deg, #bc1888 0%, #cc2366 25%, #dc2743 50%, #e6683c 75%, #f09433 100%)',
            },
            transition: 'all 0.3s ease',
            '& svg': {
              color: 'white',
              fontSize: '1.5rem'
            }
          }}
        >
          <Instagram />
        </IconButton>
      </Box>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          width: '100%',
          zIndex: 1100
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <Menu />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 'bold',
              letterSpacing: 1,
            }}
          >
            Aditya Captures
          </Typography>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {menuItems.map((item) => (
              <Tab
                key={item.text}
                label={item.text}
                icon={item.icon}
                component={Link}
                to={item.path}
                sx={{
                  minWidth: 'auto',
                }}
              />
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: 240 }, flexShrink: { sm: 0 } }}
      >
        <SwipeableDrawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          onOpen={() => setMobileOpen(true)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </SwipeableDrawer>
      </Box>
      <Toolbar />
    </>
  );
}

function MainContent({ category = '' }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      if (!category || category === 'home') {
        setPhotos([]);
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/photos/${category}`;
        console.log('API URL:', apiUrl);
        console.log('Environment:', {
          REACT_APP_API_URL: process.env.REACT_APP_API_URL,
          NODE_ENV: process.env.NODE_ENV
        });

        const response = await fetch(apiUrl, {
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          },
          mode: 'cors',
          credentials: 'same-origin'
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));

        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const text = await response.text();
          console.log('Raw response:', text);
          try {
            data = JSON.parse(text);
            console.log('Parsed data:', data);
            // Handle the case where the data is wrapped in a "value" property
            if (data.value && Array.isArray(data.value)) {
              data = data.value;
            }
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            throw new Error('Invalid JSON response');
          }
        } else {
          console.error('Invalid content type:', contentType);
          throw new Error('Server returned non-JSON response');
        }
        
        if (!response.ok) {
          throw new Error(data.details || data.error || 'Failed to fetch photos');
        }

        console.log('Setting photos:', data.length);
        setPhotos(data);
      } catch (err) {
        console.error('Error fetching photos:', err);
        setError(err.message || 'Failed to load photos');
        setPhotos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [category]);

  if (!category || category === 'home') {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
            fontWeight: 700,
            mb: { xs: 2, sm: 3, md: 4 }
          }}
        >
          Welcome to Aditya's Captures
        </Typography>
        <Box
          component="img"
          src="https://images.unsplash.com/photo-1452587925148-ce544e77e70d?q=80&w=2070&auto=format&fit=crop"
          alt="Professional Photography Studio"
          sx={{
            width: '100%',
            maxWidth: '1200px',
            height: { xs: '300px', sm: '400px', md: '600px' },
            borderRadius: { xs: '8px', sm: '12px' },
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            mb: { xs: 2, sm: 3, md: 4 },
            transition: 'transform 0.3s ease-in-out, filter 0.3s ease-in-out',
            objectFit: 'cover',
            filter: 'brightness(0.85)',
            '&:hover': {
              transform: { sm: 'scale(1.02)' },
              filter: 'brightness(1)'
            }
          }}
        />
        <Typography 
          variant="h5" 
          sx={{ 
            mb: { xs: 3, sm: 4 },
            fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
            px: { xs: 2, sm: 0 }
          }}
        >
          Here's a glimpse of my photography journey...
        </Typography>
      </Box>
    );
  }

  return (
    <UnifiedGallery
      category={category}
      photos={photos}
      title={initialCategories[category]?.title || category}
      loading={loading}
      error={error}
    />
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Navigation />
            <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
              <Routes>
                <Route path="/" element={<MainContent />} />
                <Route path="/about" element={<About />} />
                <Route path="/portrait" element={<UnifiedGallery category="portrait" />} />
                <Route path="/product" element={<UnifiedGallery category="product" />} />
                <Route path="/landscape" element={<UnifiedGallery category="landscape" />} />
                <Route path="/architecture" element={<UnifiedGallery category="architecture" />} />
                <Route path="/contact" element={<Contact />} />
              </Routes>
            </Container>
          </Box>
        </Box>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
